// The Hedge Matrix ORION CLOSE delivery -> the domain Value (CR-20260910-001 milestone 2).
//
// This REPLACES the local close synthesis that used to live inside hg-copilot.component.ts. The
// synthesis, and the three display-name helpers with it, moved to the backend so that Maven —
// ordering a close in process — runs the same code the Close tab shows; a second copy in the
// browser would have been free to drift from the one the packages are built on.
//
// A MODULE OF ITS OWN, beside copilot-mapper (domain -> wire) and copilot-transactions-view, and
// for the same two reasons those are: it is pure, and a spec must be able to import it WITHOUT
// dragging an Angular component and its whole import graph into the spec compilation.
//
// What it does NOT do is the point of it. The titles arrive already composed, the action arrives
// already decided, the underlying arrives on the leg. All three are part of the parity gate, so
// the browser carries them and re-decides nothing.

import { Leg, parseLegActionType, SolutionContent } from './copilot-domain';
import { HmCloseLegDto, HmCloseSolutionDto } from './copilot-operations';
import { getShortUUID } from '../../utils';

export function toCloseSolutionContent(s: HmCloseSolutionDto): SolutionContent {
    const legs = (s.legs || []).map(toCloseLeg);
    return {
        // The close carries no skill identity — there is no skill. The hedge id is the stable,
        // meaningful identity here, and it keeps the shape the card pipeline already understands.
        skillId: 'hm-close',
        skillUniqueId: 'hm-close-' + s.targetHedgeId + '-' + getShortUUID(),
        displayName: s.title,
        shortDescription: 'Closes This Hedge (' + legs.length + (legs.length === 1 ? ' Leg).' : ' Legs).'),
        extendedDescription: 'Generated From This Hedge — One Closing Transaction Per Held Leg. '
            + 'No Suggestion Service is Called For a Close.',
        legs,
        // A close is one hedge's own closing legs — no block structure to break out, exactly as
        // before. The card renders `legs` flat.
        cards: []
    };
}

// One close leg. Note what is NOT happening here: the action is taken as delivered rather than
// re-parsed, and the underlying is read off the leg rather than re-derived from the ticker. The
// backend decided both from the held position, which knows things the ticker does not.
export function toCloseLeg(l: HmCloseLegDto): Leg {
    const parsed = parseLegActionType(l.action);
    return {
        quantity: l.quantity,
        ticker: l.ticker,
        tickerDisplayName: l.displayName,
        action: l.action,
        side: (l.side as any) || (parsed ? parsed.side : 'sell'),
        effect: (l.effect as any) || (parsed ? parsed.effect : 'close'),
        asset: l.asset || null,
        groupId: l.groupId || undefined,
        // The structured snapshot the read-only transactions view renders its decomposed
        // Type / Strike / Expiration columns from, without re-reading the grid — a previewed close
        // has already netted the held leg to zero in the working state, so the grid can no longer
        // answer these.
        optionType: l.optionType,
        strike: l.strike,
        expiry: l.expiry,
        hedgeLabel: l.hedgeLabel,
        hedgeColor: l.hedgeColour
    };
}

// ---- Refusal name -> what the trader reads ------------------------------------------------------
//
// ⚠ WHY THIS EXISTS AT ALL, and it is the L78 shape. `settleCollapse` toasts `failureDetail`
// VERBATIM, and the Adjust engine has always put a plain SENTENCE there ("This portfolio holds
// hedges in more than one expiration (...)"). Nothing ever wrote that down, because nothing ever had
// to: the Adjust backend simply always did it. The Close's contract, by contrast, defines a CLOSED
// LIST OF NAMES on the wire (H1 section 3) — the right decision for a wire, and the wrong thing to
// show a trader. Without this map, closing a hedge that was acquired a moment earlier raises a toast
// reading "UnknownHedgeId".
//
// The local close could never produce a refusal at all, so there was no way to notice.
//
// ⚠ THE WIRE IS UNTOUCHED. The backend still sends the name; Maven and the log still read the name.
// This is presentation, and it lives panel-side where the panel's other copy already lives.
//
// ⚠ AN UNRECOGNISED NAME IS SHOWN, NOT SWALLOWED. A refusal this map has not been taught is still a
// refusal the trader must see — returning a generic sentence would hide a new backend behaviour
// behind reassuring words.
export function closeRefusalText(refusal: string | null): string | null {
    switch (refusal) {
        case 'MissingPortfolio':
            return 'No Portfolio is Loaded, so There Are no Hedges to Close.';
        case 'NoHedges':
            return 'There Are no Hedges to Close — the Book Came Through Empty, or no Hedge Was Chosen.';
        case 'MissingDates':
            return 'Choose at Least One Date to Close.';
        case 'DateNotOnBook':
            return 'One of the Dates You Chose is no Longer on the Book. Re-Open the Close Tab And Choose Again.';
        case 'UnknownHedgeId':
            return 'One of the Hedges You Chose is no Longer on the Book. Re-Open the Close Tab And Choose Again.';
        default:
            return refusal;   // never swallowed
    }
}
